import {
  AlertTriangle,
  BarChart3,
  Boxes,
  ClipboardList,
  Download,
  Eye,
  EyeOff,
  FileText,
  Gauge,
  Image,
  LifeBuoy,
  Loader2,
  Paintbrush,
  Pencil,
  Plus,
  ReceiptText,
  Settings,
  Shield,
  Trash2,
  Upload
} from "lucide-react";
import { Component, lazy, Suspense, useEffect, useState, type ChangeEvent, type ErrorInfo, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AdminStlSnapshotViewer } from "../components/AdminStlSnapshotViewer";
import { Button } from "../components/Button";
import { StatusPill } from "../components/StatusPill";
import {
  apiFetch,
  API_BASE,
  formatCurrency,
  resolveAssetUrl,
  type HeroContent,
  type Product
} from "../lib/api";
import { useAppState } from "../state/AppState";

const STLViewer = lazy(() => import("../components/STLViewer").then((module) => ({ default: module.STLViewer })));

type DashboardStats = {
  total_revenue: number;
  total_orders: number;
  active_jobs: number;
  failed_payments: number;
  revenue_change: number;
  orders_change: number;
  revenue_by_day: Array<{ date: string; revenue: number }>;
};

type AdminOrderSummary = {
  id: number;
  order_id?: string;
  customer?: string;
  email?: string;
  material?: string;
  weight_grams?: number;
  amount?: number;
  status?: string;
  progress_percent?: number;
  progress_label?: string;
  payment_status?: string;
  date?: string;
  stls?: Array<{ name: string; link: string }>;
  order_metadata?: Record<string, unknown>;
  extra_requests?: string;
};

type AdminOrderDetail = {
  id: number;
  order_number?: string;
  status?: string;
  progress_percent?: number;
  progress_label?: string;
  payment_status?: string;
  base_cost?: number;
  delivery_cost?: number;
  total_amount?: number;
  order_metadata?: Record<string, unknown>;
  extra_requests?: string;
  shipping?: Record<string, unknown>;
  cart_items?: Array<Record<string, unknown>>;
  delivery_address?: Record<string, unknown>;
  billing_address?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
  user?: {
    id?: number;
    full_name?: string;
    email?: string;
    phone?: string;
    created_at?: string;
  };
  model_file?: {
    id?: number;
    filename?: string;
    weight_grams?: number;
    volume_mm3?: number;
    stl_file?: string;
    stl_preview?: string;
    stl_download?: string;
  } | null;
  payments?: Array<{
    id: number;
    provider?: string;
    status?: string;
    amount?: number;
    currency?: string;
    gateway_order_id?: string;
    gateway_payment_id?: string;
    failure_reason?: string;
    created_at?: string;
  }>;
};

type LogFile = {
  name: string;
  feature?: string;
  week?: string;
  size: number;
  modified: number;
};

type LogFeature = {
  key: string;
  label: string;
};

type PricingRow = {
  name: string;
  price: string;
};

type ShippingSettings = {
  shiprocket_enabled: boolean;
  shiprocket_shipment_enabled: boolean;
  shiprocket_token?: string;
  origin_address: Record<string, string>;
  methods: Record<string, { label: string; fallback_price: number; fallback_eta: string }>;
  tax_settings: { enabled: boolean; gst_rate: number; seller_state: string };
};

const defaultShippingSettings: ShippingSettings = {
  shiprocket_enabled: false,
  shiprocket_shipment_enabled: false,
  shiprocket_token: "",
  origin_address: { name: "", phone: "", email: "", line1: "", line2: "", city: "", state: "", country: "India", pincode: "" },
  methods: {
    standard: { label: "Standard Delivery", fallback_price: 60, fallback_eta: "2-3 business days" },
    fast: { label: "Fast Delivery", fallback_price: 120, fallback_eta: "1-2 business days" },
    store_pickup: { label: "Store Pickup", fallback_price: 0, fallback_eta: "Pickup today" }
  },
  tax_settings: { enabled: true, gst_rate: 18, seller_state: "Karnataka" }
};

function normalizeShippingSettings(raw: ShippingSettings | Record<string, unknown> | null | undefined): ShippingSettings {
  const source = raw && typeof raw === "object" ? raw : {};
  const originSource = source.origin_address && typeof source.origin_address === "object" ? source.origin_address as Record<string, unknown> : {};
  const methodsSource = source.methods && typeof source.methods === "object" ? source.methods as Record<string, unknown> : {};
  const taxSource = source.tax_settings && typeof source.tax_settings === "object" ? source.tax_settings as Record<string, unknown> : {};
  const gstRateRaw = taxSource.gst_rate;
  const gstRate = typeof gstRateRaw === "number"
    ? gstRateRaw
    : typeof gstRateRaw === "string" && gstRateRaw.trim()
      ? Number(gstRateRaw)
      : defaultShippingSettings.tax_settings.gst_rate;

  return {
    shiprocket_enabled: Boolean(source.shiprocket_enabled),
    shiprocket_shipment_enabled: Boolean(source.shiprocket_shipment_enabled),
    shiprocket_token: typeof source.shiprocket_token === "string" ? source.shiprocket_token : "",
    origin_address: Object.fromEntries(
      Object.entries(defaultShippingSettings.origin_address).map(([key, fallback]) => [
        key,
        typeof originSource[key] === "string" ? String(originSource[key] || "") : fallback
      ])
    ),
    methods: Object.fromEntries(
      Object.entries(defaultShippingSettings.methods).map(([key, fallback]) => {
        const current = methodsSource[key] && typeof methodsSource[key] === "object" ? methodsSource[key] as Record<string, unknown> : {};
        const fallbackPriceRaw = current.fallback_price;
        const fallbackPrice = typeof fallbackPriceRaw === "number"
          ? fallbackPriceRaw
          : typeof fallbackPriceRaw === "string" && fallbackPriceRaw.trim()
            ? Number(fallbackPriceRaw)
            : fallback.fallback_price;
        return [key, {
          label: typeof current.label === "string" && current.label.trim() ? current.label : fallback.label,
          fallback_price: Number.isFinite(fallbackPrice) ? fallbackPrice : fallback.fallback_price,
          fallback_eta: typeof current.fallback_eta === "string" ? current.fallback_eta : fallback.fallback_eta
        }];
      })
    ),
    tax_settings: {
      enabled: typeof taxSource.enabled === "boolean" ? taxSource.enabled : defaultShippingSettings.tax_settings.enabled,
      gst_rate: Number.isFinite(gstRate) ? gstRate : defaultShippingSettings.tax_settings.gst_rate,
      seller_state: typeof taxSource.seller_state === "string" && taxSource.seller_state.trim() ? taxSource.seller_state : defaultShippingSettings.tax_settings.seller_state
    }
  };
}

type PaymentGatewayConfig = {
  active?: string;
  cod_enabled?: boolean;
  razorpay_enabled?: boolean;
  phonepe_enabled?: boolean;
  available_providers?: string[];
};

type MaterialRow = {
  name: string;
  colorsText: string;
  tooltip: string;
  tooltip_image: string;
};

type InfillRow = {
  label: string;
  value: string;
  percent: string;
  tooltip: string;
  tooltip_image: string;
};

type PatternRow = {
  label: string;
  value: string;
  tooltip: string;
  tooltip_image: string;
};

type SupportRow = {
  label: string;
  value: string;
};

type TitleTooltipRow = {
  title: string;
  tooltip: string;
  tooltip_image: string;
};

type EmailQuotaConfig = {
  max_daily: number;
  burst_limit: number;
  cooldown_mins: number;
};

type EmailTemplateConfig = {
  quote_subject?: string;
  quote_body?: string;
  quote_pdf?: string;
  order_subject?: string;
  order_body?: string;
  order_pdf?: string;
  footer_email?: string;
};

type TelegramConfig = {
  is_active?: boolean;
  include_order_number?: boolean;
  include_email?: boolean;
  include_cost?: boolean;
  include_grams?: boolean;
  include_model_url?: boolean;
  include_extra_info?: boolean;
};

type AboutUsConfig = {
  printer_name?: string;
  printer_description?: string;
  story_text?: string;
  how_it_works_steps?: string[];
  why_choose_us?: string[];
  speed_value?: string;
  speed_unit?: string;
  volume_value?: string;
  volume_unit?: string;
  nozzle?: string;
  filament?: string;
  colors?: number;
  auto_cal?: boolean;
  stl_file_url?: string;
  vision_text?: string;
  map_embed_url?: string;
  map_latitude?: number;
  map_longitude?: number;
  what_we_do_title?: string;
  what_we_do_text?: string;
  contact_email?: string;
  contact_phone?: string;
  contact_instagram?: string;
};

type BrandingConfig = {
  logo_url?: string | null;
  favicon_url?: string | null;
  app_name?: string;
};

type ThemeConfig = {
  accent_primary?: string;
  accent_secondary?: string;
  default_theme?: "auto" | "light" | "dark";
  bg_root?: string;
  bg_surface?: string;
  text_primary?: string;
  text_secondary?: string;
};

type FaqEntry = {
  id: number;
  question: string;
  answer: string;
  category: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
};

type ProductFormState = {
  id?: number;
  name: string;
  subtitle: string;
  description: string;
  price: string;
  is_active: boolean;
  color_options: Array<{ label: string; hex: string }>;
  remove_image: boolean;
  remove_stl: boolean;
};

const sectionMeta = [
  { key: "overview", label: "Overview", icon: BarChart3, copy: "Revenue, active jobs, failed payments, and recent orders." },
  { key: "orders", label: "Orders", icon: ClipboardList, copy: "Order status, progress, payment state, and STL downloads." },
  { key: "products", label: "Products", icon: Boxes, copy: "Catalog products, images, STL files, and active state." },
  { key: "settings", label: "Settings", icon: Settings, copy: "Pricing, shipping, payments, slicer, email, and Telegram." },
  { key: "content", label: "About Us", icon: FileText, copy: "Homepage hero, products page title, and about page content." },
  { key: "legal", label: "Legal", icon: Shield, copy: "Privacy, terms, refund, return, and shipping policy pages." },
  { key: "branding", label: "Branding", icon: Image, copy: "Logo, favicon, and app name." },
  { key: "theme", label: "Theme", icon: Paintbrush, copy: "Design tokens and default theme." },
  { key: "faq", label: "FAQ", icon: LifeBuoy, copy: "FAQ entries and custom FAQ categories." },
  { key: "logs", label: "Logs", icon: ReceiptText, copy: "Application logs and Telegram logs." },
  { key: "deep-clean", label: "Deep Clean", icon: AlertTriangle, copy: "Danger zone for deleting orders and payments." }
];

function emptyProductForm(): ProductFormState {
  return {
    name: "",
    subtitle: "",
    description: "",
    price: "",
    is_active: true,
    color_options: [{ label: "White", hex: "#FFFFFF" }],
    remove_image: false,
    remove_stl: false
  };
}

class AdminErrorBoundary extends Component<{ children: ReactNode; resetKey: string }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Admin panel render failed", error, info);
  }

  componentDidUpdate(previousProps: { resetKey: string }) {
    if (previousProps.resetKey !== this.props.resetKey && this.state.failed) {
      this.setState({ failed: false });
    }
  }

  render() {
    if (this.state.failed) {
      return (
        <AdminCard title="Admin panel error">
          <p className="text-muted text-sm">Something went wrong while rendering this admin section. Refresh the page or switch sections.</p>
        </AdminCard>
      );
    }
    return this.props.children;
  }
}

export function AdminPage({ section }: { section?: string }) {
  const params = useParams<{ section?: string; orderId?: string }>();
  const { isAdmin, user } = useAppState();
  const current = params.orderId ? "orders" : normalizeSection(section || params.section || "overview");

  if (!user || !isAdmin) {
    return (
      <section className="admin-page">
        <div className="admin-empty-state">
          <Shield size={34} aria-hidden="true" />
          <h1>Admin access required</h1>
          <p>Log in with an admin account to manage orders, products, pricing, content, and site settings.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="admin-page">
      <div className="admin-page-heading">
        <div>
          <p className="text-upper text-accent">Admin</p>
          <h1>{params.orderId ? "Order Detail" : sectionTitle(current)}</h1>
          <p>{params.orderId ? "Inspect the full order payload, STL files, and production state." : sectionDescription(current)}</p>
        </div>
        <StatusPill tone="info">Live API</StatusPill>
      </div>

      <AdminSectionNav current={current} />

      <AdminErrorBoundary resetKey={`${current}:${params.orderId || ""}`}>
        {current === "overview" ? <OverviewPanel token={user.token!} /> : null}
        {current === "orders" && params.orderId ? <OrderDetailPanel token={user.token!} orderId={params.orderId} /> : null}
        {current === "orders" && !params.orderId ? <OrdersPanel token={user.token!} /> : null}
        {current === "products" ? <ProductsPanel token={user.token!} /> : null}
        {current === "settings" ? <SettingsPanel token={user.token!} /> : null}
        {current === "content" ? <ContentPanel token={user.token!} /> : null}
        {current === "legal" ? <LegalPanel token={user.token!} /> : null}
        {current === "branding" ? <BrandingPanel token={user.token!} /> : null}
        {current === "theme" ? <ThemePanel token={user.token!} /> : null}
        {current === "faq" ? <FaqPanel token={user.token!} /> : null}
        {current === "logs" ? <LogsPanel token={user.token!} /> : null}
        {current === "deep-clean" ? <DeepCleanPanel token={user.token!} /> : null}
      </AdminErrorBoundary>
    </section>
  );
}

function OverviewPanel({ token }: { token: string }) {
  const { addToast } = useAppState();
  const [dashboard, setDashboard] = useState<DashboardStats | null>(null);
  const [orders, setOrders] = useState<AdminOrderSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      apiFetch<DashboardStats>("/admin/dashboard", { token }),
      apiFetch<AdminOrderSummary[]>("/admin/orders", { token })
    ])
      .then(([dashboardData, ordersData]) => {
        if (cancelled) return;
        setDashboard(dashboardData);
        setOrders(ordersData.slice(0, 5));
      })
      .catch((error) => {
        if (!cancelled) addToast(error instanceof Error ? error.message : "Failed to load overview", "error");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [addToast, token]);

  if (loading) return <LoadingCard label="Loading dashboard" />;

  return (
    <>
      <div className="metric-grid">
        <Metric icon={<ReceiptText size={20} />} label="Revenue" value={formatCurrency(dashboard?.total_revenue || 0)} sub={`${dashboard?.revenue_change || 0}% vs last 30 days`} />
        <Metric icon={<ClipboardList size={20} />} label="Orders" value={String(dashboard?.total_orders ?? 0)} sub={`${dashboard?.orders_change || 0}% vs last 30 days`} />
        <Metric icon={<Gauge size={20} />} label="Active Jobs" value={String(dashboard?.active_jobs ?? 0)} sub="Orders in queue or printing" />
        <Metric icon={<AlertTriangle size={20} />} label="Failed Payments" value={String(dashboard?.failed_payments ?? 0)} sub="Retry or investigate" />
      </div>

      <AdminCard title="Revenue Trend">
        <RevenueChart data={dashboard?.revenue_by_day || []} />
      </AdminCard>

      <AdminCard title="Recent Orders" action={<Link to="/admin/orders">View all</Link>}>
        <OrdersTable orders={orders} />
      </AdminCard>
    </>
  );
}

function OrdersPanel({ token }: { token: string }) {
  const { addToast } = useAppState();
  const [orders, setOrders] = useState<AdminOrderSummary[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = () => {
    setLoading(true);
    apiFetch<AdminOrderSummary[]>("/admin/orders", { token })
      .then(setOrders)
      .catch((error) => addToast(error instanceof Error ? error.message : "Failed to load orders", "error"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
  }, [token]);

  if (loading) return <LoadingCard label="Loading orders" />;

  return (
    <AdminCard title="Order Management" action={<StatusPill tone="neutral">{orders.length} orders</StatusPill>}>
      <OrdersTable orders={orders} token={token} onUpdated={reload} interactive />
    </AdminCard>
  );
}

function OrderDetailPanel({ orderId, token }: { orderId: string; token: string }) {
  const { addToast } = useAppState();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<AdminOrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingStatus, setSavingStatus] = useState(false);

  const reload = () => {
    setLoading(true);
    apiFetch<AdminOrderDetail>(`/admin/orders/${encodeURIComponent(orderId)}`, { token })
      .then(setDetail)
      .catch((error) => addToast(error instanceof Error ? error.message : "Failed to load order detail", "error"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
  }, [orderId, token]);

  async function updateStatus(nextStatus: string) {
    setSavingStatus(true);
    try {
      const data = await apiFetch<{ status: string; progress_percent: number; progress_label: string }>(
        `/admin/orders/${encodeURIComponent(orderId)}/status`,
        {
          method: "PUT",
          token,
          body: JSON.stringify({ status: nextStatus })
        }
      );
      setDetail((current) => current ? { ...current, status: data.status, progress_percent: data.progress_percent, progress_label: data.progress_label } : current);
      addToast("Order status updated", "success");
    } catch (error) {
      addToast(error instanceof Error ? error.message : "Failed to update order", "error");
    } finally {
      setSavingStatus(false);
    }
  }

  if (loading) return <LoadingCard label="Loading order detail" />;
  if (!detail) return <EmptyAdminMessage message="Order not found." />;

  const items = Array.isArray(detail.cart_items) ? detail.cart_items : [];
  const metadata = detail.order_metadata || {};

  return (
    <>
      <AdminCard
        title={`Order ${detail.order_number || detail.id}`}
        action={<Button variant="ghost" size="sm" onClick={() => navigate("/admin/orders")}>Back to orders</Button>}
      >
        <div className="admin-summary-grid">
          <SummaryTile label="Status" value={detail.progress_label || detail.status || "Received"} />
          <SummaryTile label="Payment" value={detail.payment_status || "Pending"} />
          <SummaryTile label="Base Cost" value={formatCurrency(detail.base_cost || 0)} />
          <SummaryTile label="Delivery" value={formatCurrency(detail.delivery_cost || 0)} />
          <SummaryTile label="Total" value={formatCurrency(detail.total_amount || 0)} />
        </div>
        <div className="admin-inline-form">
          <div className="form-group">
            <label htmlFor="admin-order-status">Production Status</label>
            <select
              id="admin-order-status"
              className="form-control"
              value={detail.status || "received"}
              onChange={(event) => updateStatus(event.currentTarget.value)}
              disabled={savingStatus}
            >
              {["received", "queued", "printing", "completed", "shipped"].map((value) => (
                <option key={value} value={value}>{toLabel(value)}</option>
              ))}
            </select>
          </div>
          {savingStatus ? <Loader2 className="spin" size={16} aria-hidden="true" /> : null}
        </div>
      </AdminCard>

      <div className="admin-grid-2">
        <AdminCard title="Customer Details">
          <KeyValue label="Name" value={detail.user?.full_name || "—"} />
          <KeyValue label="Email" value={detail.user?.email || "—"} />
          <KeyValue label="Phone" value={detail.user?.phone || "—"} />
          <KeyValue label="Customer Since" value={formatDateTime(detail.user?.created_at)} />
        </AdminCard>
        <AdminCard title="Addresses">
          <AddressBlock title="Delivery" value={detail.delivery_address} />
          <AddressBlock title="Billing" value={detail.billing_address} />
        </AdminCard>
      </div>

      <AdminCard title="Order Settings">
        <div className="admin-grid-2">
          <div>
            <strong className="admin-subtitle">Order Metadata</strong>
            <div className="admin-code-block">{JSON.stringify(metadata, null, 2)}</div>
          </div>
          <div>
            <strong className="admin-subtitle">Extra Requests</strong>
            <div className="admin-note">{detail.extra_requests || "No extra requests."}</div>
          </div>
        </div>
      </AdminCard>

      {detail.model_file ? (
        <AdminCard title="Primary STL File">
          <div className="admin-stl-grid">
            <div className="admin-stl-view">
              <Suspense fallback={<ViewerLoading />}>
                <STLViewer url={resolveAssetUrl(detail.model_file.stl_preview || detail.model_file.stl_file)} color="Blue" mode="quote" interactive autoRotate={false} />
              </Suspense>
            </div>
            <div className="admin-stl-meta">
              <KeyValue label="Filename" value={detail.model_file.filename || "—"} />
              <KeyValue label="Weight" value={detail.model_file.weight_grams ? `${Number(detail.model_file.weight_grams).toFixed(2)} g` : "—"} />
              <KeyValue label="Volume" value={detail.model_file.volume_mm3 ? `${Number(detail.model_file.volume_mm3).toFixed(2)} mm³` : "—"} />
              {detail.model_file.stl_download ? (
                <Button type="button" onClick={() => window.open(resolveAssetUrl(detail.model_file?.stl_download), "_blank")}>
                  Download STL
                </Button>
              ) : null}
            </div>
          </div>
        </AdminCard>
      ) : null}

      <AdminCard title="Cart Items">
        {items.length ? (
          <div className="admin-order-items">
            {items.map((item, index) => (
              <OrderItemCard key={index} item={item} />
            ))}
          </div>
        ) : (
          <p className="muted">This order has no cart items.</p>
        )}
      </AdminCard>

      <AdminCard title="Payment Records">
        {detail.payments?.length ? (
          <div className="admin-order-items">
            {detail.payments.map((payment) => (
              <div className="admin-payment-card" key={payment.id}>
                <div className="admin-payment-head">
                  <strong>{(payment.provider || "manual").toUpperCase()}</strong>
                  <StatusPill tone={payment.status === "failed" ? "warning" : "info"}>{payment.status || "pending"}</StatusPill>
                </div>
                <KeyValue label="Amount" value={`${payment.amount || 0} ${payment.currency || "INR"}`} />
                <KeyValue label="Gateway Order" value={payment.gateway_order_id || "—"} />
                <KeyValue label="Gateway Payment" value={payment.gateway_payment_id || "—"} />
                <KeyValue label="Failure Reason" value={payment.failure_reason || "—"} />
                <KeyValue label="Created" value={formatDateTime(payment.created_at)} />
              </div>
            ))}
          </div>
        ) : (
          <p className="muted">No payment records found.</p>
        )}
      </AdminCard>
    </>
  );
}

function ProductsPanel({ token }: { token: string }) {
  const { addToast } = useAppState();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<ProductFormState>(emptyProductForm());
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [stlFile, setStlFile] = useState<File | null>(null);
  const [snapshotFile, setSnapshotFile] = useState<File | null>(null);
  const [snapshotPreview, setSnapshotPreview] = useState("");
  const editingProduct = form.id ? products.find((entry) => entry.id === form.id) : undefined;

  const reload = () => {
    setLoading(true);
    apiFetch<Product[]>("/admin/products", { token })
      .then(setProducts)
      .catch((error) => addToast(error instanceof Error ? error.message : "Failed to load products", "error"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
  }, [token]);

  function openCreateForm() {
    setForm(emptyProductForm());
    setImageFile(null);
    setStlFile(null);
    setSnapshotFile(null);
    setSnapshotPreview("");
    setFormOpen(true);
  }

  function openEditForm(product: Product) {
    setForm({
      id: product.id,
      name: product.name,
      subtitle: product.subtitle || "",
      description: product.description || "",
      price: String(product.price || ""),
      is_active: product.is_active !== false,
      color_options: product.color_options?.length ? product.color_options.map((entry) => ({ label: entry.label || "", hex: entry.hex || "" })) : [{ label: "White", hex: "#FFFFFF" }],
      remove_image: false,
      remove_stl: false
    });
    setImageFile(null);
    setStlFile(null);
    setSnapshotFile(null);
    setSnapshotPreview("");
    setFormOpen(true);
  }

  function updateColor(index: number, key: "label" | "hex", value: string) {
    setForm((current) => ({
      ...current,
      color_options: current.color_options.map((entry, entryIndex) => entryIndex === index ? { ...entry, [key]: value } : entry)
    }));
  }

  async function saveProduct() {
    if (!form.name.trim()) {
      addToast("Product name is required", "error");
      return;
    }

    setSubmitting(true);
    const body = new FormData();
    body.append("name", form.name.trim());
    body.append("subtitle", form.subtitle.trim());
    body.append("description", form.description.trim());
    body.append("price", form.price || "0");
    body.append("is_active", String(form.is_active));
    body.append("color_options", JSON.stringify(form.color_options.filter((entry) => entry.label.trim()).map((entry) => ({ label: entry.label.trim(), hex: entry.hex.trim() }))));
    if (imageFile) body.append("image", imageFile);
    else if (snapshotFile) body.append("image", snapshotFile);
    if (stlFile) body.append("stl_file", stlFile);
    if (form.remove_image) body.append("remove_image", "true");
    if (form.remove_stl) body.append("remove_stl", "true");

    try {
      const path = form.id ? `/admin/products/${form.id}` : "/admin/products";
      await apiFetch(path, {
        method: form.id ? "PUT" : "POST",
        token,
        body
      });
      addToast(form.id ? "Product updated" : "Product created", "success");
      setFormOpen(false);
      reload();
    } catch (error) {
      addToast(error instanceof Error ? error.message : "Failed to save product", "error");
    } finally {
      setSubmitting(false);
    }
  }

  async function toggleActive(product: Product) {
    try {
      await apiFetch(`/admin/products/${product.id}`, {
        method: "PUT",
        token,
        body: JSON.stringify({ is_active: product.is_active === false })
      });
      reload();
    } catch (error) {
      addToast(error instanceof Error ? error.message : "Failed to update product", "error");
    }
  }

  async function deleteProduct(product: Product) {
    if (!window.confirm(`Delete ${product.name}?`)) return;
    try {
      await apiFetch(`/admin/products/${product.id}`, {
        method: "DELETE",
        token
      });
      addToast("Product deleted", "success");
      reload();
    } catch (error) {
      addToast(error instanceof Error ? error.message : "Failed to delete product", "error");
    }
  }

  return (
    <>
      <AdminCard title="Product Management" action={<Button size="sm" icon={<Plus size={14} />} onClick={openCreateForm}>Add Product</Button>}>
        {loading ? <LoadingInline label="Loading products" /> : null}
        {!loading ? (
          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Name</th>
                  <th>Subtitle</th>
                  <th>Price</th>
                  <th>Colors</th>
                  <th>STL</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.id}>
                    <td>{product.image ? <img className="admin-table-thumb" src={resolveAssetUrl(product.image)} alt="" /> : <span className="muted">—</span>}</td>
                    <td><strong>{product.name}</strong></td>
                    <td>{product.subtitle || <span className="muted">—</span>}</td>
                    <td>{formatCurrency(product.price)}</td>
                    <td className="admin-colors-inline">
                      {product.color_options?.length ? product.color_options.map((entry, index) => (
                        <span key={`${entry.label}-${index}`} className="admin-color-chip" title={entry.label}>
                          <span className="admin-color-chip-dot" style={{ background: entry.hex || "#d1d5db" }} />
                          {entry.label}
                        </span>
                      )) : <span className="muted">—</span>}
                    </td>
                    <td>{product.stl_file ? <StatusPill tone="info">STL</StatusPill> : <span className="muted">—</span>}</td>
                    <td><StatusPill tone={product.is_active === false ? "neutral" : "info"}>{product.is_active === false ? "Inactive" : "Active"}</StatusPill></td>
                    <td>
                      <div className="admin-table-actions">
                        <Button size="sm" variant="ghost" icon={<Pencil size={14} />} onClick={() => openEditForm(product)}>Edit</Button>
                        <Button size="sm" variant="ghost" icon={product.is_active === false ? <Eye size={14} /> : <EyeOff size={14} />} onClick={() => toggleActive(product)}>
                          {product.is_active === false ? "Activate" : "Hide"}
                        </Button>
                        <Button size="sm" variant="danger" icon={<Trash2 size={14} />} onClick={() => deleteProduct(product)}>Delete</Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!products.length && !loading ? (
                  <tr><td colSpan={8}>No products found.</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
        ) : null}
      </AdminCard>

      {formOpen ? (
        <AdminCard title={form.id ? "Edit Product" : "Create Product"} action={<Button size="sm" variant="ghost" onClick={() => setFormOpen(false)}>Close</Button>}>
          <div className="admin-form-grid">
            <InputField label="Name" value={form.name} onChange={(value) => setForm((current) => ({ ...current, name: value }))} />
            <InputField label="Subtitle" value={form.subtitle} onChange={(value) => setForm((current) => ({ ...current, subtitle: value }))} />
            <TextAreaField label="Description" value={form.description} rows={4} onChange={(value) => setForm((current) => ({ ...current, description: value }))} />
            <InputField label="Price" type="number" value={form.price} onChange={(value) => setForm((current) => ({ ...current, price: value }))} />
            <CheckboxField label="Active Product" checked={form.is_active} onChange={(checked) => setForm((current) => ({ ...current, is_active: checked }))} />
          </div>

          <div className="settings-block">
            <h3>Catalog Image And STL</h3>
            <div className="admin-grid-2">
              <div className="form-group">
                <label htmlFor="admin-product-image">Product Image</label>
                {form.id && !imageFile ? <MediaPreview label="Current image" url={resolveAssetUrl(editingProduct?.image)} /> : null}
                <input id="admin-product-image" className="form-control" type="file" accept="image/*" onChange={(event) => setImageFile(event.currentTarget.files?.[0] || null)} />
                {form.id ? <CheckboxField label="Remove current image" checked={form.remove_image} onChange={(checked) => setForm((current) => ({ ...current, remove_image: checked }))} /> : null}
              </div>
              <div className="form-group">
                <label htmlFor="admin-product-stl">STL File</label>
                {editingProduct?.stl_file && !stlFile && !form.remove_stl ? (
                  <div className="admin-note">
                    Current STL: <a href={resolveAssetUrl(editingProduct.stl_file)} target="_blank" rel="noreferrer">open file</a>
                  </div>
                ) : null}
                <input id="admin-product-stl" className="form-control" type="file" accept=".stl" onChange={(event) => setStlFile(event.currentTarget.files?.[0] || null)} />
                {form.id ? <CheckboxField label="Remove current STL" checked={form.remove_stl} onChange={(checked) => setForm((current) => ({ ...current, remove_stl: checked }))} /> : null}
              </div>
            </div>
            <div className="admin-note">If you do not upload a normal image, the captured STL snapshot will be submitted as the product image.</div>
            <AdminStlSnapshotViewer
              file={stlFile}
              url={stlFile || form.remove_stl ? null : resolveAssetUrl(editingProduct?.stl_file)}
              sourceName={stlFile?.name || editingProduct?.name || "product.stl"}
              onSnapshotReady={(file, previewUrl) => {
                setSnapshotFile(file);
                setSnapshotPreview(previewUrl);
              }}
            />
            {snapshotPreview ? (
              <div className="admin-note">Snapshot ready. This image will be used if no explicit product image is uploaded.</div>
            ) : null}
          </div>

          <div className="settings-block">
            <h3>Color Options</h3>
            <div className="admin-dynamic-stack">
              {/* Keep row keys stable while typing. Using the label in the key remounts
                  the input on every keystroke, which is why the field felt broken. */}
              {form.color_options.map((entry, index) => (
                <div className="admin-dynamic-row" key={index}>
                  <input className="form-control" type="text" placeholder="Label" value={entry.label} onChange={(event) => updateColor(index, "label", event.currentTarget.value)} />
                  <input className="form-control" type="text" placeholder="#HEX" value={entry.hex} onChange={(event) => updateColor(index, "hex", event.currentTarget.value)} />
                  <input className="admin-color-input" type="color" value={normalizeHex(entry.hex)} onChange={(event) => updateColor(index, "hex", event.currentTarget.value)} />
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<Trash2 size={14} />}
                    onClick={() =>
                      setForm((current) => {
                        const remaining = current.color_options.filter((_, entryIndex) => entryIndex !== index);
                        return {
                          ...current,
                          color_options: remaining.length ? remaining : [{ label: "", hex: "#d1d5db" }]
                        };
                      })
                    }
                  />
                </div>
              ))}
            </div>
            <Button size="sm" variant="ghost" icon={<Plus size={14} />} onClick={() => setForm((current) => ({ ...current, color_options: [...current.color_options, { label: "", hex: "#d1d5db" }] }))}>
              Add Color
            </Button>
          </div>

          <div className="admin-form-actions">
            <Button loading={submitting} onClick={saveProduct}>{form.id ? "Save Product" : "Create Product"}</Button>
          </div>
        </AdminCard>
      ) : null}
    </>
  );
}

function SettingsPanel({ token }: { token: string }) {
  const { addToast } = useAppState();
  const [loading, setLoading] = useState(true);
  const [pricingRows, setPricingRows] = useState<PricingRow[]>([]);
  const [shipping, setShipping] = useState<ShippingSettings>(defaultShippingSettings);
  const [payment, setPayment] = useState<PaymentGatewayConfig>({ active: "manual", cod_enabled: true, phonepe_enabled: false });
  const [materials, setMaterials] = useState<MaterialRow[]>([]);
  const [infillRows, setInfillRows] = useState<InfillRow[]>([]);
  const [patternRows, setPatternRows] = useState<PatternRow[]>([]);
  const [supportRows, setSupportRows] = useState<SupportRow[]>([]);
  const [titleTooltips, setTitleTooltips] = useState<TitleTooltipRow[]>([]);
  const [emailQuotas, setEmailQuotas] = useState<EmailQuotaConfig>({ max_daily: 15, burst_limit: 3, cooldown_mins: 10 });
  const [emailTemplate, setEmailTemplate] = useState<EmailTemplateConfig>({});
  const [telegram, setTelegram] = useState<TelegramConfig>({});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      apiFetch<Record<string, number>>("/admin/upload-price", { token }),
      apiFetch<ShippingSettings>("/admin/shipping-settings", { token }),
      apiFetch<PaymentGatewayConfig>("/admin/payment-gateway", { token }),
      apiFetch<Record<string, unknown>>("/admin/slicer-options", { token }),
      apiFetch<EmailQuotaConfig>("/admin/email-quotas", { token }),
      apiFetch<EmailTemplateConfig>("/admin/email-template", { token }),
      apiFetch<TelegramConfig>("/admin/telegram-config", { token })
    ])
      .then(([pricing, shippingData, paymentData, slicerData, quotasData, templateData, telegramData]) => {
        if (cancelled) return;
        setPricingRows(Object.entries(pricing).map(([name, price]) => ({ name: name.toUpperCase(), price: String(price) })));
        setShipping(normalizeShippingSettings(shippingData));
        setPayment(paymentData);
        setMaterials(((slicerData.materials as Array<Record<string, unknown>>) || []).map((entry) => ({
          name: String(entry.name || ""),
          colorsText: formatSlicerColors(entry.colors),
          tooltip: String(entry.tooltip || ""),
          tooltip_image: String(entry.tooltip_image || "")
        })));
        setInfillRows(((slicerData.infill_options as Array<Record<string, unknown>>) || []).map((entry) => ({
          label: String(entry.label || ""),
          value: String(entry.value || ""),
          percent: String(entry.percent ?? ""),
          tooltip: String(entry.tooltip || ""),
          tooltip_image: String(entry.tooltip_image || "")
        })));
        setPatternRows(((slicerData.infill_patterns as Array<Record<string, unknown>>) || []).map((entry) => ({
          label: String(entry.label || ""),
          value: String(entry.value || ""),
          tooltip: String(entry.tooltip || ""),
          tooltip_image: String(entry.tooltip_image || "")
        })));
        setSupportRows(((slicerData.support_styles as Array<Record<string, unknown>>) || []).map((entry) => ({
          label: String(entry.label || ""),
          value: String(entry.value || "")
        })));
        setTitleTooltips((((slicerData.title_tooltips as Array<Record<string, unknown>>) || [])).map((entry) => ({
          title: String(entry.title || ""),
          tooltip: String(entry.tooltip || ""),
          tooltip_image: String(entry.tooltip_image || "")
        })));
        setEmailQuotas(quotasData);
        setEmailTemplate(templateData);
        setTelegram(telegramData);
      })
      .catch((error) => {
        if (!cancelled) addToast(error instanceof Error ? error.message : "Failed to load settings", "error");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [addToast, token]);

  async function uploadHelperImage(file: File) {
    const body = new FormData();
    body.append("image", file);
    const data = await apiFetch<{ url: string }>("/admin/upload-image", { method: "POST", token, body });
    return data.url;
  }

  async function savePricing() {
    const payload = pricingRows.reduce<Record<string, number>>((acc, row) => {
      if (!row.name.trim()) return acc;
      acc[row.name.trim()] = Number(row.price || 0);
      return acc;
    }, {});
    try {
      await apiFetch("/admin/upload-price", { method: "PUT", token, body: JSON.stringify(payload) });
      addToast("Pricing updated", "success");
    } catch (error) {
      addToast(error instanceof Error ? error.message : "Failed to save pricing", "error");
    }
  }

  async function saveShipping() {
    try {
      await apiFetch("/admin/shipping-settings", { method: "PUT", token, body: JSON.stringify(shipping) });
      addToast("Shipping settings updated", "success");
    } catch (error) {
      addToast(error instanceof Error ? error.message : "Failed to save shipping settings", "error");
    }
  }

  async function savePayment() {
    try {
      await apiFetch("/admin/payment-gateway", { method: "PUT", token, body: JSON.stringify(payment) });
      addToast("Payment gateway updated", "success");
    } catch (error) {
      addToast(error instanceof Error ? error.message : "Failed to save payment settings", "error");
    }
  }

  async function saveSlicer() {
    const payload = {
      materials: materials.filter((entry) => entry.name.trim()).map((entry) => ({
        name: entry.name.trim(),
        colors: parseSlicerColors(entry.colorsText),
        ...(entry.tooltip.trim() ? { tooltip: entry.tooltip.trim() } : {}),
        ...(entry.tooltip_image.trim() ? { tooltip_image: entry.tooltip_image.trim() } : {})
      })),
      infill_options: infillRows.filter((entry) => entry.label.trim() && entry.value.trim()).map((entry) => ({
        label: entry.label.trim(),
        value: entry.value.trim(),
        percent: Number(entry.percent || 0),
        ...(entry.tooltip.trim() ? { tooltip: entry.tooltip.trim() } : {}),
        ...(entry.tooltip_image.trim() ? { tooltip_image: entry.tooltip_image.trim() } : {})
      })),
      infill_patterns: patternRows.filter((entry) => entry.label.trim()).map((entry) => ({
        label: entry.label.trim(),
        value: entry.value.trim() || slugify(entry.label),
        ...(entry.tooltip.trim() ? { tooltip: entry.tooltip.trim() } : {}),
        ...(entry.tooltip_image.trim() ? { tooltip_image: entry.tooltip_image.trim() } : {})
      })),
      support_styles: supportRows.filter((entry) => entry.label.trim()).map((entry) => ({
        label: entry.label.trim(),
        value: entry.value.trim() || slugify(entry.label)
      })),
      title_tooltips: titleTooltips.filter((entry) => entry.title.trim() && entry.tooltip.trim()).map((entry) => ({
        title: entry.title.trim(),
        tooltip: entry.tooltip.trim(),
        tooltip_image: entry.tooltip_image.trim()
      }))
    };
    try {
      await apiFetch("/admin/slicer-options", { method: "PUT", token, body: JSON.stringify(payload) });
      addToast("Slicer options updated", "success");
    } catch (error) {
      addToast(error instanceof Error ? error.message : "Failed to save slicer options", "error");
    }
  }

  async function saveEmailQuotas() {
    try {
      await apiFetch("/admin/email-quotas", { method: "PUT", token, body: JSON.stringify(emailQuotas) });
      addToast("Email quotas updated", "success");
    } catch (error) {
      addToast(error instanceof Error ? error.message : "Failed to save email quotas", "error");
    }
  }

  async function saveEmailTemplate() {
    try {
      await apiFetch("/admin/email-template", { method: "PUT", token, body: JSON.stringify(emailTemplate) });
      addToast("Email template updated", "success");
    } catch (error) {
      addToast(error instanceof Error ? error.message : "Failed to save email template", "error");
    }
  }

  async function saveTelegram() {
    try {
      await apiFetch("/admin/telegram-config", { method: "PUT", token, body: JSON.stringify(telegram) });
      addToast("Telegram settings updated", "success");
    } catch (error) {
      addToast(error instanceof Error ? error.message : "Failed to save Telegram settings", "error");
    }
  }

  if (loading) return <LoadingCard label="Loading settings" />;

  return (
    <>
      <AdminCard title="Pricing Per Gram">
        <DynamicRows
          rows={pricingRows}
          onAdd={() => setPricingRows((current) => [...current, { name: "", price: "" }])}
          onRemove={(index) => setPricingRows((current) => current.filter((_, rowIndex) => rowIndex !== index))}
          renderRow={(row, index) => (
            <>
              <input className="form-control" value={row.name} placeholder="Material" onChange={(event) => {
                const value = event.currentTarget.value;
                setPricingRows((current) => current.map((entry, rowIndex) => rowIndex === index ? { ...entry, name: value } : entry));
              }} />
              <input className="form-control" type="number" value={row.price} placeholder="₹ / gram" onChange={(event) => {
                const value = event.currentTarget.value;
                setPricingRows((current) => current.map((entry, rowIndex) => rowIndex === index ? { ...entry, price: value } : entry));
              }} />
            </>
          )}
        />
        <div className="admin-form-actions"><Button onClick={savePricing}>Save Pricing</Button></div>
      </AdminCard>

      <AdminCard title="Shipping / Shiprocket">
        <div className="admin-grid-2">
          <CheckboxField label="Enable Shiprocket" checked={shipping.shiprocket_enabled} onChange={(checked) => setShipping((current) => ({ ...current, shiprocket_enabled: checked }))} />
          <CheckboxField label="Place Shiprocket shipments" checked={shipping.shiprocket_shipment_enabled} onChange={(checked) => setShipping((current) => ({ ...current, shiprocket_shipment_enabled: checked }))} />
          <div className="text-muted text-sm">Set <code>SHIPROCKET_TOKEN</code> in the backend environment. Shiprocket can calculate rates even when shipment placement is disabled.</div>
        </div>
        <div className="settings-block">
          <h3>Origin Address</h3>
          <div className="admin-form-grid">
            {Object.entries(shipping.origin_address).map(([key, value]) => (
              <InputField key={key} label={toLabel(key)} value={value} onChange={(nextValue) => setShipping((current) => ({ ...current, origin_address: { ...current.origin_address, [key]: nextValue } }))} />
            ))}
          </div>
        </div>
        <div className="settings-block">
          <h3>Shipping Methods</h3>
          <div className="admin-dynamic-stack">
            {Object.entries(shipping.methods || {}).map(([key, value]) => {
              const resolved = value || defaultShippingSettings.methods[key] || { label: "", fallback_price: 0, fallback_eta: "" };
              return (
              <div className="admin-dynamic-row" key={key}>
                <input className="form-control" value={key} disabled />
                <input className="form-control" value={resolved.label} onChange={(event) => {
                  const value = event.currentTarget.value;
                  setShipping((current) => ({ ...current, methods: { ...current.methods, [key]: { ...(current.methods?.[key] || resolved), label: value } } }));
                }} />
                <input className="form-control" type="number" value={resolved.fallback_price} onChange={(event) => {
                  const value = event.currentTarget.value;
                  setShipping((current) => ({ ...current, methods: { ...current.methods, [key]: { ...(current.methods?.[key] || resolved), fallback_price: Number(value || 0) } } }));
                }} />
                <input className="form-control" value={resolved.fallback_eta} placeholder="Fallback ETA" onChange={(event) => {
                  const value = event.currentTarget.value;
                  setShipping((current) => ({ ...current, methods: { ...current.methods, [key]: { ...(current.methods?.[key] || resolved), fallback_eta: value } } }));
                }} />
              </div>
            );})}
          </div>
        </div>
        <div className="settings-block">
          <h3>GST Settings</h3>
          <div className="admin-grid-2">
            <CheckboxField
              label="Enable GST on checkout"
              checked={shipping.tax_settings.enabled}
              onChange={(checked) => setShipping((current) => ({ ...current, tax_settings: { ...current.tax_settings, enabled: checked } }))}
            />
            <InputField
              label="GST Rate (%)"
              type="number"
              value={String(shipping.tax_settings.gst_rate)}
              onChange={(value) => setShipping((current) => ({ ...current, tax_settings: { ...current.tax_settings, gst_rate: Number(value || 0) } }))}
            />
            <InputField
              label="Seller State"
              value={shipping.tax_settings.seller_state}
              onChange={(value) => setShipping((current) => ({ ...current, tax_settings: { ...current.tax_settings, seller_state: value } }))}
            />
          </div>
        </div>
        <div className="admin-form-actions"><Button onClick={saveShipping}>Save Shipping</Button></div>
      </AdminCard>

      <AdminCard title="Payment Methods">
        <div className="admin-grid-2">
          <div className="form-group">
            <label htmlFor="admin-payment-active">Default Method</label>
            <select id="admin-payment-active" className="form-control" value={payment.active || "manual"} onChange={(event) => {
              const value = event.currentTarget.value;
              setPayment((current) => ({ ...current, active: value }));
            }}>
              {["manual", "phonepe"].map((value) => (
                <option key={value} value={value}>{value === "manual" ? "Cash on delivery" : "PhonePe"}</option>
              ))}
            </select>
          </div>
          <CheckboxField label="Cash on delivery enabled" checked={Boolean(payment.cod_enabled)} onChange={(checked) => setPayment((current) => ({ ...current, cod_enabled: checked }))} />
          <CheckboxField label="PhonePe enabled" checked={Boolean(payment.phonepe_enabled)} onChange={(checked) => setPayment((current) => ({ ...current, phonepe_enabled: checked }))} />
        </div>
        <div className="admin-form-actions"><Button onClick={savePayment}>Save Payment Settings</Button></div>
      </AdminCard>

      <AdminCard title="Slicer Quote Options">
        <SettingsArrayCard title="Materials" addLabel="Add Material" onAdd={() => setMaterials((current) => [...current, { name: "", colorsText: "", tooltip: "", tooltip_image: "" }])}>
          {materials.map((row, index) => (
            <div className="admin-array-block" key={`material-${index}`}>
              <div className="admin-dynamic-row">
                <input className="form-control" placeholder="Material" value={row.name} onChange={(event) => {
                  const value = event.currentTarget.value;
                  setMaterials((current) => updateAt(current, index, { ...row, name: value }));
                }} />
                <input className="form-control" placeholder="Colors (Name:#hex, Name)" value={row.colorsText} onChange={(event) => {
                  const value = event.currentTarget.value;
                  setMaterials((current) => updateAt(current, index, { ...row, colorsText: value }));
                }} />
                <Button size="sm" variant="ghost" icon={<Trash2 size={14} />} onClick={() => setMaterials((current) => current.filter((_, rowIndex) => rowIndex !== index))} />
              </div>
              <div className="admin-dynamic-row">
                <input className="form-control" placeholder="Tooltip" value={row.tooltip} onChange={(event) => {
                  const value = event.currentTarget.value;
                  setMaterials((current) => updateAt(current, index, { ...row, tooltip: value }));
                }} />
                <UploadUrlField value={row.tooltip_image} onChange={(value) => setMaterials((current) => updateAt(current, index, { ...row, tooltip_image: value }))} onUpload={uploadHelperImage} />
              </div>
            </div>
          ))}
        </SettingsArrayCard>

        <SettingsArrayCard title="Infill Options" addLabel="Add Infill Option" onAdd={() => setInfillRows((current) => [...current, { label: "", value: "", percent: "", tooltip: "", tooltip_image: "" }])}>
          {infillRows.map((row, index) => (
            <div className="admin-array-block" key={`infill-${index}`}>
              <div className="admin-dynamic-row">
                <input className="form-control" placeholder="Label" value={row.label} onChange={(event) => {
                  const value = event.currentTarget.value;
                  setInfillRows((current) => updateAt(current, index, { ...row, label: value }));
                }} />
                <input className="form-control" placeholder="Value" value={row.value} onChange={(event) => {
                  const value = event.currentTarget.value;
                  setInfillRows((current) => updateAt(current, index, { ...row, value }));
                }} />
                <input className="form-control" type="number" placeholder="Percent" value={row.percent} onChange={(event) => {
                  const value = event.currentTarget.value;
                  setInfillRows((current) => updateAt(current, index, { ...row, percent: value }));
                }} />
                <Button size="sm" variant="ghost" icon={<Trash2 size={14} />} onClick={() => setInfillRows((current) => current.filter((_, rowIndex) => rowIndex !== index))} />
              </div>
              <div className="admin-dynamic-row">
                <input className="form-control" placeholder="Tooltip" value={row.tooltip} onChange={(event) => {
                  const value = event.currentTarget.value;
                  setInfillRows((current) => updateAt(current, index, { ...row, tooltip: value }));
                }} />
                <UploadUrlField value={row.tooltip_image} onChange={(value) => setInfillRows((current) => updateAt(current, index, { ...row, tooltip_image: value }))} onUpload={uploadHelperImage} />
              </div>
            </div>
          ))}
        </SettingsArrayCard>

        <SettingsArrayCard title="Infill Patterns" addLabel="Add Pattern" onAdd={() => setPatternRows((current) => [...current, { label: "", value: "", tooltip: "", tooltip_image: "" }])}>
          {patternRows.map((row, index) => (
            <div className="admin-array-block" key={`pattern-${index}`}>
              <div className="admin-dynamic-row">
                <input className="form-control" placeholder="Label" value={row.label} onChange={(event) => {
                  const value = event.currentTarget.value;
                  setPatternRows((current) => updateAt(current, index, { ...row, label: value }));
                }} />
                <input className="form-control" placeholder="Value" value={row.value} onChange={(event) => {
                  const value = event.currentTarget.value;
                  setPatternRows((current) => updateAt(current, index, { ...row, value }));
                }} />
                <Button size="sm" variant="ghost" icon={<Trash2 size={14} />} onClick={() => setPatternRows((current) => current.filter((_, rowIndex) => rowIndex !== index))} />
              </div>
              <div className="admin-dynamic-row">
                <input className="form-control" placeholder="Tooltip" value={row.tooltip} onChange={(event) => {
                  const value = event.currentTarget.value;
                  setPatternRows((current) => updateAt(current, index, { ...row, tooltip: value }));
                }} />
                <UploadUrlField value={row.tooltip_image} onChange={(value) => setPatternRows((current) => updateAt(current, index, { ...row, tooltip_image: value }))} onUpload={uploadHelperImage} />
              </div>
            </div>
          ))}
        </SettingsArrayCard>

        <SettingsArrayCard title="Support Styles" addLabel="Add Support Style" onAdd={() => setSupportRows((current) => [...current, { label: "", value: "" }])}>
          {supportRows.map((row, index) => (
            <div className="admin-dynamic-row" key={`support-${index}`}>
              <input className="form-control" placeholder="Label" value={row.label} onChange={(event) => {
                const value = event.currentTarget.value;
                setSupportRows((current) => updateAt(current, index, { ...row, label: value }));
              }} />
              <input className="form-control" placeholder="Value" value={row.value} onChange={(event) => {
                const value = event.currentTarget.value;
                setSupportRows((current) => updateAt(current, index, { ...row, value }));
              }} />
              <Button size="sm" variant="ghost" icon={<Trash2 size={14} />} onClick={() => setSupportRows((current) => current.filter((_, rowIndex) => rowIndex !== index))} />
            </div>
          ))}
        </SettingsArrayCard>

        <SettingsArrayCard title="Title Tooltips" addLabel="Add Title Tooltip" onAdd={() => setTitleTooltips((current) => [...current, { title: "", tooltip: "", tooltip_image: "" }])}>
          {titleTooltips.map((row, index) => (
            <div className="admin-array-block" key={`title-tooltip-${index}`}>
              <div className="admin-dynamic-row">
                <input className="form-control" placeholder="Title" value={row.title} onChange={(event) => {
                  const value = event.currentTarget.value;
                  setTitleTooltips((current) => updateAt(current, index, { ...row, title: value }));
                }} />
                <input className="form-control" placeholder="Tooltip" value={row.tooltip} onChange={(event) => {
                  const value = event.currentTarget.value;
                  setTitleTooltips((current) => updateAt(current, index, { ...row, tooltip: value }));
                }} />
                <Button size="sm" variant="ghost" icon={<Trash2 size={14} />} onClick={() => setTitleTooltips((current) => current.filter((_, rowIndex) => rowIndex !== index))} />
              </div>
              <UploadUrlField value={row.tooltip_image} onChange={(value) => setTitleTooltips((current) => updateAt(current, index, { ...row, tooltip_image: value }))} onUpload={uploadHelperImage} />
            </div>
          ))}
        </SettingsArrayCard>

        <div className="admin-form-actions"><Button onClick={saveSlicer}>Save Slicer Options</Button></div>
      </AdminCard>

      <AdminCard title="Email Quotas">
        <div className="admin-grid-2">
          <InputField label="Max Daily" type="number" value={String(emailQuotas.max_daily)} onChange={(value) => setEmailQuotas((current) => ({ ...current, max_daily: Number(value || 0) }))} />
          <InputField label="Burst Limit" type="number" value={String(emailQuotas.burst_limit)} onChange={(value) => setEmailQuotas((current) => ({ ...current, burst_limit: Number(value || 0) }))} />
          <InputField label="Cooldown Minutes" type="number" value={String(emailQuotas.cooldown_mins)} onChange={(value) => setEmailQuotas((current) => ({ ...current, cooldown_mins: Number(value || 0) }))} />
        </div>
        <div className="admin-form-actions"><Button onClick={saveEmailQuotas}>Save Email Quotas</Button></div>
      </AdminCard>

      <AdminCard title="Email Template">
        <div className="admin-form-grid">
          <InputField label="Quote Subject" value={emailTemplate.quote_subject || ""} onChange={(value) => setEmailTemplate((current) => ({ ...current, quote_subject: value }))} />
          <InputField label="Order Subject" value={emailTemplate.order_subject || ""} onChange={(value) => setEmailTemplate((current) => ({ ...current, order_subject: value }))} />
          <InputField label="Footer Email" value={emailTemplate.footer_email || ""} onChange={(value) => setEmailTemplate((current) => ({ ...current, footer_email: value }))} />
          <TextAreaField label="Quote Body" rows={6} value={emailTemplate.quote_body || ""} onChange={(value) => setEmailTemplate((current) => ({ ...current, quote_body: value }))} />
          <TextAreaField label="Order Body" rows={6} value={emailTemplate.order_body || ""} onChange={(value) => setEmailTemplate((current) => ({ ...current, order_body: value }))} />
          <TextAreaField label="Quote PDF HTML" rows={8} value={emailTemplate.quote_pdf || ""} onChange={(value) => setEmailTemplate((current) => ({ ...current, quote_pdf: value }))} />
          <TextAreaField label="Order PDF HTML" rows={8} value={emailTemplate.order_pdf || ""} onChange={(value) => setEmailTemplate((current) => ({ ...current, order_pdf: value }))} />
        </div>
        <div className="admin-form-actions"><Button onClick={saveEmailTemplate}>Save Email Template</Button></div>
      </AdminCard>

      <AdminCard title="Telegram Notifications">
        <div className="admin-grid-2">
          <CheckboxField label="Enable Telegram Notifications" checked={Boolean(telegram.is_active)} onChange={(checked) => setTelegram((current) => ({ ...current, is_active: checked }))} />
          <CheckboxField label="Include Order Number" checked={telegram.include_order_number !== false} onChange={(checked) => setTelegram((current) => ({ ...current, include_order_number: checked }))} />
          <CheckboxField label="Include Email" checked={telegram.include_email !== false} onChange={(checked) => setTelegram((current) => ({ ...current, include_email: checked }))} />
          <CheckboxField label="Include Cost" checked={telegram.include_cost !== false} onChange={(checked) => setTelegram((current) => ({ ...current, include_cost: checked }))} />
          <CheckboxField label="Include Weight" checked={telegram.include_grams !== false} onChange={(checked) => setTelegram((current) => ({ ...current, include_grams: checked }))} />
          <CheckboxField label="Include Model URL" checked={telegram.include_model_url !== false} onChange={(checked) => setTelegram((current) => ({ ...current, include_model_url: checked }))} />
          <CheckboxField label="Include Extra Info" checked={telegram.include_extra_info !== false} onChange={(checked) => setTelegram((current) => ({ ...current, include_extra_info: checked }))} />
        </div>
        <div className="admin-form-actions"><Button onClick={saveTelegram}>Save Telegram Settings</Button></div>
      </AdminCard>
    </>
  );
}

function ContentPanel({ token }: { token: string }) {
  const { addToast } = useAppState();
  const [loading, setLoading] = useState(true);
  const [hero, setHero] = useState<HeroContent>({});
  const [productsPageTitle, setProductsPageTitle] = useState("Our 3D Printable Models");
  const [about, setAbout] = useState<AboutUsConfig>({});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      apiFetch<HeroContent>("/admin/hero-content", { token }),
      apiFetch<{ title?: string }>("/admin/products-page", { token }),
      apiFetch<AboutUsConfig>("/admin/about-us", { token })
    ])
      .then(([heroData, productsPage, aboutData]) => {
        if (cancelled) return;
        setHero(heroData);
        setProductsPageTitle(productsPage.title || "Our 3D Printable Models");
        setAbout(aboutData);
      })
      .catch((error) => {
        if (!cancelled) addToast(error instanceof Error ? error.message : "Failed to load content settings", "error");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [addToast, token]);

  async function saveHero() {
    try {
      await apiFetch("/admin/hero-content", {
        method: "PUT",
        token,
        body: JSON.stringify({
          ...hero,
          typewriter_texts: splitLines(joinLines(hero.typewriter_texts))
        })
      });
      addToast("Hero content updated", "success");
    } catch (error) {
      addToast(error instanceof Error ? error.message : "Failed to save hero content", "error");
    }
  }

  async function saveProductsPage() {
    try {
      await apiFetch("/admin/products-page", { method: "PUT", token, body: JSON.stringify({ title: productsPageTitle }) });
      addToast("Products page title updated", "success");
    } catch (error) {
      addToast(error instanceof Error ? error.message : "Failed to save products page title", "error");
    }
  }

  async function uploadHelperFile(file: File) {
    const body = new FormData();
    body.append("file", file);
    const data = await apiFetch<{ url: string }>("/admin/upload-image", { method: "POST", token, body });
    return data.url;
  }

  async function saveAbout() {
    try {
      await apiFetch("/admin/about-us", {
        method: "PUT",
        token,
        body: JSON.stringify({
          ...about,
          how_it_works_steps: splitLines(joinLines(about.how_it_works_steps)),
          why_choose_us: splitLines(joinLines(about.why_choose_us))
        })
      });
      addToast("About page content updated", "success");
    } catch (error) {
      addToast(error instanceof Error ? error.message : "Failed to save about content", "error");
    }
  }

  if (loading) return <LoadingCard label="Loading content settings" />;

  return (
    <>
      <AdminCard title="Homepage Hero">
        <div className="admin-form-grid">
          <InputField label="Eyebrow Text" value={hero.eyebrow_text || ""} onChange={(value) => setHero((current) => ({ ...current, eyebrow_text: value }))} />
          <TextAreaField label="Typewriter Lines" rows={5} value={joinLines(hero.typewriter_texts)} onChange={(value) => setHero((current) => ({ ...current, typewriter_texts: splitLines(value) }))} />
          <InputField label="Upload Title" value={hero.upload_title || ""} onChange={(value) => setHero((current) => ({ ...current, upload_title: value }))} />
          <InputField label="Upload Subtitle" value={hero.upload_subtitle || ""} onChange={(value) => setHero((current) => ({ ...current, upload_subtitle: value }))} />
        </div>
        <div className="admin-form-actions"><Button onClick={saveHero}>Save Hero Content</Button></div>
      </AdminCard>

      <AdminCard title="Products Page Title">
        <InputField label="Public Products Page Title" value={productsPageTitle} onChange={setProductsPageTitle} />
        <div className="admin-form-actions"><Button onClick={saveProductsPage}>Save Products Page</Button></div>
      </AdminCard>

      <AdminCard title="About Page Content">
        <div className="admin-form-grid">
          <InputField label="Printer Name" value={about.printer_name || ""} onChange={(value) => setAbout((current) => ({ ...current, printer_name: value }))} />
          <InputField label="Nozzle" value={about.nozzle || ""} onChange={(value) => setAbout((current) => ({ ...current, nozzle: value }))} />
          <InputField label="Filament" value={about.filament || ""} onChange={(value) => setAbout((current) => ({ ...current, filament: value }))} />
          <InputField label="Color Count" type="number" value={String(about.colors ?? 0)} onChange={(value) => setAbout((current) => ({ ...current, colors: Number(value || 0) }))} />
          <InputField label="Speed Value" value={about.speed_value || ""} onChange={(value) => setAbout((current) => ({ ...current, speed_value: value }))} />
          <InputField label="Speed Unit" value={about.speed_unit || ""} onChange={(value) => setAbout((current) => ({ ...current, speed_unit: value }))} />
          <InputField label="Volume Value" value={about.volume_value || ""} onChange={(value) => setAbout((current) => ({ ...current, volume_value: value }))} />
          <InputField label="Volume Unit" value={about.volume_unit || ""} onChange={(value) => setAbout((current) => ({ ...current, volume_unit: value }))} />
          <CheckboxField label="Auto Calibration" checked={Boolean(about.auto_cal)} onChange={(checked) => setAbout((current) => ({ ...current, auto_cal: checked }))} />
          <div className="form-group">
            <label>About STL File</label>
            <UploadUrlField
              accept=".stl"
              placeholder="About STL URL"
              uploadLabel="Upload STL"
              successMessage="STL uploaded"
              value={about.stl_file_url || ""}
              onChange={(value) => setAbout((current) => ({ ...current, stl_file_url: value }))}
              onUpload={uploadHelperFile}
            />
          </div>
          <InputField label="Map Embed URL" value={about.map_embed_url || ""} onChange={(value) => setAbout((current) => ({ ...current, map_embed_url: value }))} />
          <InputField label="Map Latitude" type="number" value={String(about.map_latitude ?? "")} onChange={(value) => setAbout((current) => ({ ...current, map_latitude: Number(value || 0) }))} />
          <InputField label="Map Longitude" type="number" value={String(about.map_longitude ?? "")} onChange={(value) => setAbout((current) => ({ ...current, map_longitude: Number(value || 0) }))} />
          <InputField label="What We Do Title" value={about.what_we_do_title || ""} onChange={(value) => setAbout((current) => ({ ...current, what_we_do_title: value }))} />
          <InputField label="Contact Email" value={about.contact_email || ""} onChange={(value) => setAbout((current) => ({ ...current, contact_email: value }))} />
          <InputField label="Contact Phone" value={about.contact_phone || ""} onChange={(value) => setAbout((current) => ({ ...current, contact_phone: value }))} />
          <InputField label="Instagram" value={about.contact_instagram || ""} onChange={(value) => setAbout((current) => ({ ...current, contact_instagram: value }))} />
          <TextAreaField label="Printer Description" rows={4} value={about.printer_description || ""} onChange={(value) => setAbout((current) => ({ ...current, printer_description: value }))} />
          <TextAreaField label="Story Text" rows={5} value={about.story_text || ""} onChange={(value) => setAbout((current) => ({ ...current, story_text: value }))} />
          <TextAreaField label="Vision Text" rows={4} value={about.vision_text || ""} onChange={(value) => setAbout((current) => ({ ...current, vision_text: value }))} />
          <TextAreaField label="What We Do Text" rows={4} value={about.what_we_do_text || ""} onChange={(value) => setAbout((current) => ({ ...current, what_we_do_text: value }))} />
          <TextAreaField label="How It Works Steps" rows={4} value={joinLines(about.how_it_works_steps)} onChange={(value) => setAbout((current) => ({ ...current, how_it_works_steps: splitLines(value) }))} />
          <TextAreaField label="Why Choose Us Lines" rows={4} value={joinLines(about.why_choose_us)} onChange={(value) => setAbout((current) => ({ ...current, why_choose_us: splitLines(value) }))} />
        </div>
        <div className="admin-form-actions"><Button onClick={saveAbout}>Save About Page</Button></div>
      </AdminCard>
    </>
  );
}

function LegalPanel({ token }: { token: string }) {
  const { addToast } = useAppState();
  const [loading, setLoading] = useState(true);
  const [docs, setDocs] = useState<Record<string, string>>({
    "privacy-policy": "",
    terms: "",
    "refund-policy": "",
    "return-policy": "",
    "shipping-policy": ""
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      "privacy-policy",
      "terms",
      "refund-policy",
      "return-policy",
      "shipping-policy"
    ].map((key) => apiFetch<{ markdown?: string }>(`/admin/${key}`, { token })))
      .then((responses) => {
        if (cancelled) return;
        setDocs({
          "privacy-policy": responses[0].markdown || "",
          terms: responses[1].markdown || "",
          "refund-policy": responses[2].markdown || "",
          "return-policy": responses[3].markdown || "",
          "shipping-policy": responses[4].markdown || ""
        });
      })
      .catch((error) => {
        if (!cancelled) addToast(error instanceof Error ? error.message : "Failed to load legal documents", "error");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [addToast, token]);

  async function saveDoc(key: string) {
    try {
      await apiFetch(`/admin/${key}`, { method: "PUT", token, body: JSON.stringify({ markdown: docs[key] || "" }) });
      addToast(`${toLabel(key)} updated`, "success");
    } catch (error) {
      addToast(error instanceof Error ? error.message : `Failed to save ${key}`, "error");
    }
  }

  if (loading) return <LoadingCard label="Loading legal documents" />;

  return (
    <>
      {Object.entries(docs).map(([key, value]) => (
        <AdminCard key={key} title={toLabel(key)}>
          <TextAreaField label={`${toLabel(key)} Markdown`} rows={12} value={value} onChange={(nextValue) => setDocs((current) => ({ ...current, [key]: nextValue }))} />
          <div className="admin-form-actions"><Button onClick={() => saveDoc(key)}>Save {toLabel(key)}</Button></div>
        </AdminCard>
      ))}
    </>
  );
}

function BrandingPanel({ token }: { token: string }) {
  const { addToast } = useAppState();
  const [loading, setLoading] = useState(true);
  const [branding, setBranding] = useState<BrandingConfig>({ app_name: "ProtoDeccan", logo_url: null, favicon_url: null });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [faviconFile, setFaviconFile] = useState<File | null>(null);

  const reload = () => {
    setLoading(true);
    apiFetch<BrandingConfig>("/admin/branding", { token })
      .then(setBranding)
      .catch((error) => addToast(error instanceof Error ? error.message : "Failed to load branding", "error"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
  }, [token]);

  async function saveAppName() {
    try {
      await apiFetch("/admin/branding", { method: "POST", token, body: JSON.stringify({ app_name: branding.app_name || "ProtoDeccan" }) });
      addToast("App name updated", "success");
      reload();
    } catch (error) {
      addToast(error instanceof Error ? error.message : "Failed to save app name", "error");
    }
  }

  async function uploadFile(kind: "logo" | "favicon", file: File | null) {
    if (!file) return;
    const body = new FormData();
    body.append(kind, file);
    try {
      await apiFetch("/admin/branding", { method: "POST", token, body });
      addToast(`${toLabel(kind)} uploaded`, "success");
      if (kind === "logo") setLogoFile(null);
      else setFaviconFile(null);
      reload();
    } catch (error) {
      addToast(error instanceof Error ? error.message : `Failed to upload ${kind}`, "error");
    }
  }

  if (loading) return <LoadingCard label="Loading branding" />;

  return (
    <div className="admin-grid-2">
      <AdminCard title="Website Logo">
        <MediaPreview label="Current logo" url={resolveAssetUrl(branding.logo_url)} />
        <InputField label="App Name" value={branding.app_name || "ProtoDeccan"} onChange={(value) => setBranding((current) => ({ ...current, app_name: value }))} />
        <input className="form-control" type="file" accept="image/*,.svg" onChange={(event) => setLogoFile(event.currentTarget.files?.[0] || null)} />
        <div className="admin-form-actions">
          <Button onClick={saveAppName}>Save App Name</Button>
          <Button variant="ghost" icon={<Upload size={14} />} onClick={() => uploadFile("logo", logoFile)} disabled={!logoFile}>Upload Logo</Button>
        </div>
      </AdminCard>

      <AdminCard title="Favicon">
        <MediaPreview label="Current favicon" url={resolveAssetUrl(branding.favicon_url)} square />
        <input className="form-control" type="file" accept=".ico,image/*,.svg" onChange={(event) => setFaviconFile(event.currentTarget.files?.[0] || null)} />
        <div className="admin-form-actions">
          <Button variant="ghost" icon={<Upload size={14} />} onClick={() => uploadFile("favicon", faviconFile)} disabled={!faviconFile}>Upload Favicon</Button>
        </div>
      </AdminCard>
    </div>
  );
}

function ThemePanel({ token }: { token: string }) {
  const { addToast } = useAppState();
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState<ThemeConfig>({});

  useEffect(() => {
    setLoading(true);
    apiFetch<ThemeConfig>("/admin/theme", { token })
      .then(setTheme)
      .catch((error) => addToast(error instanceof Error ? error.message : "Failed to load theme config", "error"))
      .finally(() => setLoading(false));
  }, [addToast, token]);

  async function saveTheme() {
    try {
      await apiFetch("/admin/theme", { method: "PUT", token, body: JSON.stringify(theme) });
      addToast("Theme updated", "success");
    } catch (error) {
      addToast(error instanceof Error ? error.message : "Failed to save theme config", "error");
    }
  }

  if (loading) return <LoadingCard label="Loading theme config" />;

  return (
    <AdminCard title="Theme Tokens">
      <div className="admin-form-grid">
        <ColorField label="Accent Primary" value={theme.accent_primary || "#4D93F8"} onChange={(value) => setTheme((current) => ({ ...current, accent_primary: value }))} />
        <ColorField label="Accent Secondary" value={theme.accent_secondary || "#2A51C1"} onChange={(value) => setTheme((current) => ({ ...current, accent_secondary: value }))} />
        <ColorField label="Background Root" value={theme.bg_root || "#121212"} onChange={(value) => setTheme((current) => ({ ...current, bg_root: value }))} />
        <ColorField label="Background Surface" value={theme.bg_surface || "#1A1A1A"} onChange={(value) => setTheme((current) => ({ ...current, bg_surface: value }))} />
        <ColorField label="Text Primary" value={theme.text_primary || "#FFFFFF"} onChange={(value) => setTheme((current) => ({ ...current, text_primary: value }))} />
        <ColorField label="Text Secondary" value={theme.text_secondary || "#A0A0A0"} onChange={(value) => setTheme((current) => ({ ...current, text_secondary: value }))} />
        <div className="form-group">
          <label htmlFor="admin-default-theme">Default Theme</label>
          <select id="admin-default-theme" className="form-control" value={theme.default_theme || "auto"} onChange={(event) => {
            const value = event.currentTarget.value as ThemeConfig["default_theme"];
            setTheme((current) => ({ ...current, default_theme: value }));
          }}>
            <option value="auto">Auto</option>
            <option value="light">Light</option>
            <option value="dark">Dark</option>
          </select>
        </div>
      </div>
      <div className="admin-form-actions"><Button onClick={saveTheme}>Save Theme</Button></div>
    </AdminCard>
  );
}

function FaqPanel({ token }: { token: string }) {
  const { addToast } = useAppState();
  const [loading, setLoading] = useState(true);
  const [faqs, setFaqs] = useState<FaqEntry[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [form, setForm] = useState<{ id?: number; question: string; answer: string; category: string; is_active: boolean }>({
    question: "",
    answer: "",
    category: "",
    is_active: true
  });

  const reload = () => {
    setLoading(true);
    Promise.all([
      apiFetch<FaqEntry[]>("/admin/faq", { token }),
      apiFetch<{ categories: string[] }>("/admin/faq-categories", { token })
    ])
      .then(([faqData, categoryData]) => {
        setFaqs(faqData);
        setCategories(categoryData.categories || []);
        setForm((current) => ({ ...current, category: current.category || categoryData.categories?.[0] || "" }));
      })
      .catch((error) => addToast(error instanceof Error ? error.message : "Failed to load FAQ data", "error"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
  }, [token]);

  async function saveCategories() {
    try {
      await apiFetch("/admin/faq-categories", { method: "PUT", token, body: JSON.stringify({ categories }) });
      addToast("FAQ categories updated", "success");
      reload();
    } catch (error) {
      addToast(error instanceof Error ? error.message : "Failed to save FAQ categories", "error");
    }
  }

  async function saveFaq() {
    try {
      const path = form.id ? `/admin/faq/${form.id}` : "/admin/faq";
      await apiFetch(path, { method: form.id ? "PUT" : "POST", token, body: JSON.stringify(form) });
      addToast(form.id ? "FAQ updated" : "FAQ created", "success");
      setForm({ question: "", answer: "", category: categories[0] || "", is_active: true });
      reload();
    } catch (error) {
      addToast(error instanceof Error ? error.message : "Failed to save FAQ", "error");
    }
  }

  async function deleteFaq(id: number) {
    if (!window.confirm("Delete this FAQ?")) return;
    try {
      await apiFetch(`/admin/faq/${id}`, { method: "DELETE", token });
      addToast("FAQ deleted", "success");
      reload();
    } catch (error) {
      addToast(error instanceof Error ? error.message : "Failed to delete FAQ", "error");
    }
  }

  return (
    <>
      <AdminCard title="FAQ Categories">
        {loading ? <LoadingInline label="Loading FAQ categories" /> : null}
        <DynamicRows
          rows={categories}
          onAdd={() => setCategories((current) => [...current, ""])}
          onRemove={(index) => setCategories((current) => current.filter((_, categoryIndex) => categoryIndex !== index))}
          renderRow={(row, index) => (
            <input className="form-control" value={row} placeholder="Category" onChange={(event) => {
              const value = event.currentTarget.value;
              setCategories((current) => current.map((entry, categoryIndex) => categoryIndex === index ? value : entry));
            }} />
          )}
        />
        <div className="admin-form-actions"><Button onClick={saveCategories}>Save Categories</Button></div>
      </AdminCard>

      <AdminCard title={form.id ? "Edit FAQ" : "Create FAQ"}>
        <div className="admin-form-grid">
          <InputField label="Question" value={form.question} onChange={(value) => setForm((current) => ({ ...current, question: value }))} />
          <div className="form-group">
            <label htmlFor="admin-faq-category">Category</label>
            <select id="admin-faq-category" className="form-control" value={form.category} onChange={(event) => {
              const value = event.currentTarget.value;
              setForm((current) => ({ ...current, category: value }));
            }}>
              {categories.map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
          <TextAreaField label="Answer" rows={6} value={form.answer} onChange={(value) => setForm((current) => ({ ...current, answer: value }))} />
          <CheckboxField label="Active FAQ" checked={form.is_active} onChange={(checked) => setForm((current) => ({ ...current, is_active: checked }))} />
        </div>
        <div className="admin-form-actions">
          <Button onClick={saveFaq}>{form.id ? "Save FAQ" : "Create FAQ"}</Button>
          {form.id ? <Button variant="ghost" onClick={() => setForm({ question: "", answer: "", category: categories[0] || "", is_active: true })}>Cancel Edit</Button> : null}
        </div>
      </AdminCard>

      <AdminCard title="FAQ Entries">
        {loading ? <LoadingInline label="Loading FAQs" /> : null}
        <div className="data-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Question</th>
                <th>Category</th>
                <th>Status</th>
                <th>Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {faqs.map((faq) => (
                <tr key={faq.id}>
                  <td>{faq.question}</td>
                  <td>{faq.category}</td>
                  <td><StatusPill tone={faq.is_active ? "info" : "neutral"}>{faq.is_active ? "Active" : "Hidden"}</StatusPill></td>
                  <td>{formatDateTime(faq.updated_at)}</td>
                  <td>
                    <div className="admin-table-actions">
                      <Button size="sm" variant="ghost" icon={<Pencil size={14} />} onClick={() => setForm({ id: faq.id, question: faq.question, answer: faq.answer, category: faq.category, is_active: faq.is_active })}>Edit</Button>
                      <Button size="sm" variant="danger" icon={<Trash2 size={14} />} onClick={() => deleteFaq(faq.id)}>Delete</Button>
                    </div>
                  </td>
                </tr>
              ))}
              {!faqs.length && !loading ? <tr><td colSpan={5}>No FAQs found.</td></tr> : null}
            </tbody>
          </table>
        </div>
      </AdminCard>
    </>
  );
}

function LogsPanel({ token }: { token: string }) {
  const { addToast } = useAppState();
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState<LogFile[]>([]);
  const [features, setFeatures] = useState<LogFeature[]>([]);
  const [weeks, setWeeks] = useState<string[]>([]);
  const [selectedFeature, setSelectedFeature] = useState("");
  const [selectedWeek, setSelectedWeek] = useState("");
  const [selectedFile, setSelectedFile] = useState("");
  const [selectedContent, setSelectedContent] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (selectedFeature) params.set("feature", selectedFeature);
    if (selectedWeek) params.set("week", selectedWeek);
    const query = params.toString();
    apiFetch<{ logs: LogFile[]; features?: LogFeature[]; weeks?: string[] }>(`/admin/logs${query ? `?${query}` : ""}`, { token })
      .then((logFiles) => {
        if (cancelled) return;
        setLogs(logFiles.logs || []);
        setFeatures(logFiles.features || []);
        setWeeks(logFiles.weeks || []);
        setSelectedFile("");
        setSelectedContent("");
      })
      .catch((error) => {
        if (!cancelled) addToast(error instanceof Error ? error.message : "Failed to load logs", "error");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [addToast, selectedFeature, selectedWeek, token]);

  async function openLog(fileName: string) {
    try {
      const data = await apiFetch<{ content: string }>(`/admin/logs?file=${encodeURIComponent(fileName)}`, { token });
      setSelectedFile(fileName);
      setSelectedContent(data.content || "");
    } catch (error) {
      addToast(error instanceof Error ? error.message : "Failed to open log file", "error");
    }
  }

  async function downloadLog(fileName: string) {
    try {
      const response = await fetch(`${API_BASE}/admin/logs?file=${encodeURIComponent(fileName)}&download=1`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error(`Download failed with status ${response.status}`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      addToast(error instanceof Error ? error.message : "Failed to download log", "error");
    }
  }

  const selectedLog = logs.find((log) => log.name === selectedFile);
  const totalBytes = logs.reduce((sum, log) => sum + (log.size || 0), 0);
  const activeFeatureLabel = selectedFeature ? features.find((feature) => feature.key === selectedFeature)?.label || selectedFeature : "All Logs";

  return (
    <div className="admin-logs-page">
      <AdminCard title="Logs">
        <div className="admin-log-toolbar">
          <div>
            <div className="admin-log-kicker">Viewing</div>
            <h3>{activeFeatureLabel}</h3>
            <p>{logs.length} file{logs.length === 1 ? "" : "s"} · {formatBytes(totalBytes)}</p>
          </div>
          <select className="form-control admin-log-week-select" value={selectedWeek} onChange={(event) => setSelectedWeek(event.currentTarget.value)}>
            <option value="">All weeks</option>
            {weeks.map((week) => (
              <option key={week} value={week}>{week}</option>
            ))}
          </select>
        </div>

        <div className="admin-log-feature-row">
          <button className={!selectedFeature ? "admin-log-feature active" : "admin-log-feature"} type="button" onClick={() => setSelectedFeature("")}>All</button>
          {features.map((feature) => (
            <button className={selectedFeature === feature.key ? "admin-log-feature active" : "admin-log-feature"} key={feature.key} type="button" onClick={() => setSelectedFeature(feature.key)}>
              {feature.label}
            </button>
          ))}
        </div>
      </AdminCard>

      <div className="admin-log-workspace">
        <AdminCard title="Files">
        {loading ? <LoadingInline label="Loading logs" /> : null}
        <div className="admin-log-list">
          {logs.map((log) => (
            <div className={selectedFile === log.name ? "admin-log-item active" : "admin-log-item"} key={log.name}>
              <button type="button" onClick={() => openLog(log.name)}>
                <strong>{log.name}</strong>
                <span>{[log.feature, log.week, formatBytes(log.size), formatDateTime(new Date(log.modified * 1000).toISOString())].filter(Boolean).join(" · ")}</span>
              </button>
              <button className="admin-log-download" type="button" aria-label={`Download ${log.name}`} title={`Download ${log.name}`} onClick={() => downloadLog(log.name)}>
                <Download size={15} aria-hidden="true" />
              </button>
            </div>
          ))}
          {!logs.length && !loading ? <p className="muted">No log files found.</p> : null}
        </div>
      </AdminCard>

        <AdminCard title={selectedFile || "Log Viewer"}>
        {selectedLog ? (
          <div className="admin-log-viewer-meta">
            <span>{selectedLog.week}</span>
            <span>{formatBytes(selectedLog.size)}</span>
            <span>{formatDateTime(new Date(selectedLog.modified * 1000).toISOString())}</span>
            <button className="admin-log-download admin-log-download-text" type="button" onClick={() => downloadLog(selectedLog.name)}>
              <Download size={14} aria-hidden="true" />
              Download
            </button>
          </div>
        ) : null}
        <div className="admin-code-block admin-code-scroll">{selectedContent || "Select a log file to inspect its contents."}</div>
      </AdminCard>
      </div>
    </div>
  );
}

function DeepCleanPanel({ token }: { token: string }) {
  const { addToast } = useAppState();
  const [password, setPassword] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  async function runDeepClean() {
    setDeleting(true);
    try {
      const data = await apiFetch<{ message: string }>("/admin/deep-clean", {
        method: "DELETE",
        token,
        body: JSON.stringify({ password })
      });
      addToast(data.message || "Deep clean completed", "success");
      setConfirmOpen(false);
      setPassword("");
    } catch (error) {
      addToast(error instanceof Error ? error.message : "Deep clean failed", "error");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <AdminCard title="Deep Clean">
        <div className="admin-danger-panel">
          <AlertTriangle size={24} aria-hidden="true" />
          <div>
            <h3>Delete all orders and payments</h3>
            <p>This permanently removes order and payment records from the database. This action cannot be undone.</p>
          </div>
        </div>
        <div className="admin-form-actions">
          <Button variant="danger" icon={<Trash2 size={14} />} onClick={() => setConfirmOpen(true)}>Delete Everything</Button>
        </div>
      </AdminCard>

      {confirmOpen ? (
        <AdminModal title="Confirm Deep Clean" onClose={() => setConfirmOpen(false)}>
          <TextAreaField label="Why this is dangerous" rows={3} value="This will permanently delete all orders and payments." onChange={() => {}} disabled />
          <InputField label="Deep Clean Password" type="password" value={password} onChange={setPassword} />
          <div className="admin-form-actions">
            <Button variant="danger" loading={deleting} onClick={runDeepClean}>Confirm Delete</Button>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>Cancel</Button>
          </div>
        </AdminModal>
      ) : null}
    </>
  );
}

function OrdersTable({
  interactive = false,
  onUpdated,
  orders,
  token
}: {
  interactive?: boolean;
  onUpdated?: () => void;
  orders: AdminOrderSummary[];
  token?: string;
}) {
  const navigate = useNavigate();
  const { addToast } = useAppState();

  async function updateStatus(orderId: number, status: string) {
    if (!token) return;
    try {
      await apiFetch(`/admin/orders/${orderId}/status`, {
        method: "PUT",
        token,
        body: JSON.stringify({ status })
      });
      addToast("Order status updated", "success");
      onUpdated?.();
    } catch (error) {
      addToast(error instanceof Error ? error.message : "Failed to update order", "error");
    }
  }

  return (
    <div className="data-table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <th>Order ID</th>
            <th>Customer</th>
            <th>Material</th>
            <th>Weight</th>
            <th>Amount</th>
            <th>Status</th>
            <th>Payment</th>
            <th>Date</th>
            <th>STL</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => (
            <tr key={order.id} className="admin-table-row-clickable" onClick={() => navigate(`/admin/orders/${order.id}`)}>
              <td>{order.order_id || `#${order.id}`}</td>
              <td>{order.customer || "—"}</td>
              <td>{order.material || "—"}</td>
              <td>{order.weight_grams ? `${Number(order.weight_grams).toFixed(1)} g` : "—"}</td>
              <td>{formatCurrency(order.amount || 0)}</td>
              <td onClick={(event) => event.stopPropagation()}>
                {interactive && token ? (
                  <select className="form-control admin-compact-select" value={order.status || "received"} onChange={(event) => updateStatus(order.id, event.currentTarget.value)}>
                    {["received", "queued", "printing", "completed", "shipped"].map((value) => (
                      <option key={value} value={value}>{toLabel(value)}</option>
                    ))}
                  </select>
                ) : (
                  <StatusPill tone="info">{order.progress_label || order.status || "Received"}</StatusPill>
                )}
              </td>
              <td>{(order.payment_status || "pending").toUpperCase()}</td>
              <td>{formatDateTime(order.date)}</td>
              <td>{order.stls?.length ? `${order.stls.length} file${order.stls.length === 1 ? "" : "s"}` : "—"}</td>
            </tr>
          ))}
          {!orders.length ? <tr><td colSpan={9}>No orders found.</td></tr> : null}
        </tbody>
      </table>
    </div>
  );
}

function OrderItemCard({ item }: { item: Record<string, unknown> }) {
  const stlUrl = typeof item.stl_file === "string" ? resolveAssetUrl(item.stl_file) : "";
  const downloadUrl = typeof item.stl_download === "string" ? resolveAssetUrl(item.stl_download) : "";
  const quoteOptions = item.quote_options && typeof item.quote_options === "object" ? item.quote_options as Record<string, unknown> : {};
  const extraRequests = String(quoteOptions.extra_requests || item.extra_requests || "").trim();
  const configPairs = (
    [
      ["Material", String(item.material || quoteOptions.material || "")],
      ["Color", String(item.color || quoteOptions.color || "")],
      ["Infill", String(quoteOptions.infill || "")],
      ["Support", String(quoteOptions.support || "")],
      ["Nozzle", String(quoteOptions.nozzle || "")],
      ["Support Style", String(quoteOptions.support_style || "")],
      ["Infill Pattern", String(quoteOptions.infill_pattern || "")],
      ["Top / Bottom Layers", String(quoteOptions.top_bottom_layers || "")]
    ] as Array<[string, string]>
  ).filter(([, value]) => value !== "");

  return (
    <div className="admin-order-item-card">
      <div className="admin-order-item-preview">
        {stlUrl ? (
          <Suspense fallback={<ViewerLoading />}>
            <STLViewer url={stlUrl} color="Blue" mode="quote" interactive={false} autoRotate={false} />
          </Suspense>
        ) : item.image ? (
          <img src={resolveAssetUrl(String(item.image))} alt="" />
        ) : (
          <div className="admin-order-item-empty">No preview</div>
        )}
      </div>
      <div className="admin-order-item-body">
        <div className="admin-payment-head">
          <strong>{String(item.name || "Item")}</strong>
          <span>{formatCurrency(Number(item.price || 0) * Number(item.quantity || 1))}</span>
        </div>
        <div className="admin-inline-stats">
          <span>Qty: {String(item.quantity || 1)}</span>
          {item.weight_grams ? <span>{Number(item.weight_grams).toFixed(1)} g</span> : null}
          {item.volume_cm3 ? <span>{Number(item.volume_cm3).toFixed(1)} cm³</span> : null}
        </div>
        {configPairs.length ? (
          <div className="admin-config-pills">
            {configPairs.map(([label, value]) => (
              <span className="admin-config-pill" key={label}>{label}: {String(value)}</span>
            ))}
          </div>
        ) : null}
        {extraRequests ? <div className="admin-note">Extra requests: {extraRequests}</div> : null}
        {downloadUrl ? (
          <Button type="button" size="sm" variant="secondary" icon={<Download size={14} />} onClick={() => window.open(downloadUrl, "_blank")}>
            Download STL
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function AdminSectionNav({ current }: { current: string }) {
  return (
    <div className="admin-section-tabs" aria-label="Admin Sections">
      {sectionMeta.map((item) => (
        <Link key={item.key} className={current === item.key ? "active" : undefined} to={item.key === "overview" ? "/admin" : `/admin/${item.key}`}>
          <item.icon size={15} aria-hidden="true" />
          {item.label}
        </Link>
      ))}
    </div>
  );
}

function AdminCard({ action, children, title }: { action?: ReactNode; children: ReactNode; title: string }) {
  return (
    <div className="admin-table-card">
      <div className="panel-heading">
        <h2>{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}

function AdminModal({ children, onClose, title }: { children: ReactNode; onClose: () => void; title: string }) {
  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true">
      <div className="admin-modal-card">
        <div className="panel-heading">
          <h2>{title}</h2>
          <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Metric({ icon, label, sub, value }: { icon: ReactNode; label: string; sub: string; value: string }) {
  return (
    <article className="metric-card">
      <span aria-hidden="true">{icon}</span>
      <p>{label}</p>
      <strong>{value}</strong>
      <small>{sub}</small>
    </article>
  );
}

function SummaryTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="admin-summary-tile">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function RevenueChart({ data }: { data: Array<{ date: string; revenue: number }> }) {
  if (!data.length) return <p className="muted">No orders in the last 30 days.</p>;
  const width = 860;
  const height = 240;
  const maxValue = Math.max(...data.map((entry) => entry.revenue), 1);
  const points = data.map((entry, index) => {
    const x = (index / Math.max(data.length - 1, 1)) * (width - 40) + 20;
    const y = height - 24 - ((entry.revenue / maxValue) * (height - 48));
    return `${x},${y}`;
  }).join(" ");

  return (
    <div className="admin-chart-wrap">
      <svg viewBox={`0 0 ${width} ${height}`} className="admin-chart" role="img" aria-label="Revenue trend chart">
        <polyline points={points} fill="none" stroke="var(--accent-primary)" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
        {data.map((entry, index) => {
          const x = (index / Math.max(data.length - 1, 1)) * (width - 40) + 20;
          const y = height - 24 - ((entry.revenue / maxValue) * (height - 48));
          return <circle key={`${entry.date}-${index}`} cx={x} cy={y} r="3.5" fill="var(--accent-primary)" />;
        })}
      </svg>
      <div className="admin-chart-labels">
        <span>{data[0]?.date || ""}</span>
        <span>{data[data.length - 1]?.date || ""}</span>
      </div>
    </div>
  );
}

function InputField({
  disabled = false,
  label,
  onChange,
  type = "text",
  value
}: {
  disabled?: boolean;
  label: string;
  onChange: (value: string) => void;
  type?: string;
  value: string;
}) {
  return (
    <div className="form-group">
      <label>{label}</label>
      <input className="form-control" disabled={disabled} type={type} value={value} onChange={(event) => onChange(event.currentTarget.value)} />
    </div>
  );
}

function TextAreaField({
  disabled = false,
  label,
  onChange,
  rows = 5,
  value
}: {
  disabled?: boolean;
  label: string;
  onChange: (value: string) => void;
  rows?: number;
  value: string;
}) {
  return (
    <div className="form-group">
      <label>{label}</label>
      <textarea className="form-control admin-textarea" disabled={disabled} rows={rows} value={value} onChange={(event) => onChange(event.currentTarget.value)} />
    </div>
  );
}

function CheckboxField({ checked, label, onChange }: { checked: boolean; label: string; onChange: (checked: boolean) => void }) {
  return (
    <label className="admin-checkbox">
      <input checked={checked} type="checkbox" onChange={(event) => onChange(event.currentTarget.checked)} />
      <span>{label}</span>
    </label>
  );
}

function ColorField({ label, onChange, value }: { label: string; onChange: (value: string) => void; value: string }) {
  return (
    <div className="form-group">
      <label>{label}</label>
      <div className="admin-color-field">
        <input className="admin-color-input" type="color" value={normalizeHex(value)} onChange={(event) => onChange(event.currentTarget.value)} />
        <input className="form-control" value={value} onChange={(event) => onChange(event.currentTarget.value)} />
      </div>
    </div>
  );
}

function UploadUrlField({
  accept = "image/*",
  onChange,
  onUpload,
  placeholder = "Image URL",
  successMessage = "Image uploaded",
  uploadLabel = "Upload",
  value
}: {
  accept?: string;
  onChange: (value: string) => void;
  onUpload: (file: File) => Promise<string>;
  placeholder?: string;
  successMessage?: string;
  uploadLabel?: string;
  value: string;
}) {
  const { addToast } = useAppState();
  const [uploading, setUploading] = useState(false);

  async function handleFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.currentTarget.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await onUpload(file);
      onChange(url);
      addToast(successMessage, "success");
    } catch (error) {
      addToast(error instanceof Error ? error.message : "Upload failed", "error");
    } finally {
      setUploading(false);
      event.currentTarget.value = "";
    }
  }

  return (
    <div className="admin-upload-row">
      <input className="form-control" placeholder={placeholder} value={value} onChange={(event) => onChange(event.currentTarget.value)} />
      <label className="admin-upload-button" role="button" tabIndex={0}>
        <input hidden accept={accept} type="file" onChange={handleFile} />
        {uploading ? <Loader2 className="spin" size={14} aria-hidden="true" /> : <Upload size={14} aria-hidden="true" />}
        {uploadLabel}
      </label>
    </div>
  );
}

function DynamicRows<T>({
  onAdd,
  onRemove,
  renderRow,
  rows
}: {
  onAdd: () => void;
  onRemove: (index: number) => void;
  renderRow: (row: T, index: number) => ReactNode;
  rows: T[];
}) {
  return (
    <>
      <div className="admin-dynamic-stack">
        {rows.map((row, index) => (
          <div className="admin-dynamic-row" key={index}>
            {renderRow(row, index)}
            <Button size="sm" variant="ghost" icon={<Trash2 size={14} />} onClick={() => onRemove(index)} />
          </div>
        ))}
      </div>
      <div className="admin-form-actions">
        <Button size="sm" variant="ghost" icon={<Plus size={14} />} onClick={onAdd}>Add Row</Button>
      </div>
    </>
  );
}

function SettingsArrayCard({
  addLabel,
  children,
  onAdd,
  title
}: {
  addLabel: string;
  children: ReactNode;
  onAdd: () => void;
  title: string;
}) {
  return (
    <div className="settings-block">
      <h3>{title}</h3>
      {children}
      <div className="admin-form-actions">
        <Button size="sm" variant="ghost" icon={<Plus size={14} />} onClick={onAdd}>{addLabel}</Button>
      </div>
    </div>
  );
}

function LoadingCard({ label }: { label: string }) {
  return (
    <AdminCard title={label}>
      <LoadingInline label={label} />
    </AdminCard>
  );
}

function LoadingInline({ label }: { label: string }) {
  return (
    <div className="admin-loading-inline">
      <Loader2 className="spin" size={18} aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

function ViewerLoading() {
  return <div className="admin-viewer-loading">Loading STL preview...</div>;
}

function EmptyAdminMessage({ message }: { message: string }) {
  return (
    <AdminCard title="Unavailable">
      <p className="muted">{message}</p>
    </AdminCard>
  );
}

function MediaPreview({ label, square = false, url }: { label: string; square?: boolean; url?: string }) {
  return (
    <div className={square ? "admin-media-preview square" : "admin-media-preview"}>
      <span className="text-upper text-accent">{label}</span>
      {url ? <img src={url} alt="" /> : <div className="muted">No asset uploaded.</div>}
    </div>
  );
}

function KeyValue({ label, value }: { label: string; value: string }) {
  return (
    <div className="admin-key-value">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function AddressBlock({ title, value }: { title: string; value?: Record<string, unknown> }) {
  const lines = addressLines(value);
  return (
    <div className="admin-address-block">
      <strong className="admin-subtitle">{title}</strong>
      {lines.length ? lines.map((line) => <div key={line}>{line}</div>) : <span className="muted">No address provided.</span>}
    </div>
  );
}

function normalizeSection(value: string) {
  return sectionMeta.some((item) => item.key === value) ? value : "overview";
}

function sectionTitle(value: string) {
  return sectionMeta.find((item) => item.key === value)?.label || "Overview";
}

function sectionDescription(value: string) {
  return sectionMeta.find((item) => item.key === value)?.copy || sectionMeta[0].copy;
}

function splitLines(value: string) {
  return value.split("\n").map((entry) => entry.trim()).filter(Boolean);
}

function joinLines(value?: string[]) {
  return Array.isArray(value) ? value.join("\n") : "";
}

function updateAt<T>(items: T[], index: number, value: T) {
  return items.map((entry, entryIndex) => entryIndex === index ? value : entry);
}

function toLabel(value: string) {
  return value.replace(/[_-]+/g, " ").replace(/\b\w/g, (match) => match.toUpperCase());
}

function slugify(value: string) {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
}

function formatSlicerColors(value: unknown) {
  if (!Array.isArray(value)) return "";
  return value.map((entry) => {
    if (typeof entry === "string") return entry;
    if (entry && typeof entry === "object") {
      const row = entry as Record<string, unknown>;
      return row.hex ? `${row.name}:${row.hex}` : String(row.name || "");
    }
    return "";
  }).filter(Boolean).join(", ");
}

function parseSlicerColors(value: string) {
  return value.split(",").map((entry) => entry.trim()).filter(Boolean).map((entry) => {
    const [name, hex] = entry.split(":").map((part) => part.trim());
    return hex ? { name, hex } : name;
  });
}

function uniqueValue(value: string, index: number, array: string[]) {
  return array.indexOf(value) === index;
}

function normalizeHex(value: string) {
  return /^#[0-9a-fA-F]{6}$/.test(value) ? value : "#d1d5db";
}

function formatBytes(value: number) {
  if (!value) return "0 B";
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDateTime(value?: string) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
}

function addressLines(value?: Record<string, unknown>) {
  if (!value) return [];
  const name = String(value.name || `${value.first_name || ""} ${value.last_name || ""}`.trim() || "").trim();
  const line1 = String(value.line1 || value.address_line1 || "").trim();
  const line2 = String(value.line2 || value.address_line2 || "").trim();
  const cityState = [value.city, value.state, value.pincode || value.postal_code].filter(Boolean).join(", ");
  const phone = String(value.phone || "").trim();
  return [name, line1, line2, cityState, phone].filter(Boolean);
}
